import type { DeploymentScope } from "../custom-model/deployment";
import type { OrganizationProfileData, ProfileData } from "../profile/types";
import {
  workforceCostContextKey,
  workforceFteContextKey,
} from "../profile/workforce";
import { getBaselineDefinition } from "./registry";
import type { BaselineRecord, BaselineScope } from "./types";

// Trust ordering for merge semantics
const SOURCE_TRUST: Record<string, number> = {
  benchmark: 0,
  estimated: 1,
  customer_reported: 2,
  confirmed: 3,
};

/**
 * Serialize a BaselineScope to a stable string key.
 * undefined/null → "", {} → "", { payer: "commercial" } → "payer=commercial"
 * Multiple dims sorted alphabetically, joined with "|"
 */
export function serializeBaselineScope(scope?: BaselineScope): string {
  if (!scope) return "";
  const entries = Object.entries(scope).filter(
    ([, v]) => v !== undefined && v !== null,
  );
  if (entries.length === 0) return "";
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join("|");
}

/**
 * Compare two scopes for equality via their serialized form.
 */
export function scopesEqual(a?: BaselineScope, b?: BaselineScope): boolean {
  return serializeBaselineScope(a) === serializeBaselineScope(b);
}

/**
 * Count the number of dimensions in a scope.
 */
function scopeSize(scope?: BaselineScope): number {
  if (!scope) return 0;
  return Object.values(scope).filter((v) => v !== undefined && v !== null)
    .length;
}

/**
 * Check whether recordScope is a subset of (or equal to) queryScope.
 * An enterprise record (no scope) is always a subset of any query.
 */
function recordScopeIsSubsetOfQuery(
  recordScope: BaselineScope | undefined,
  queryScope: BaselineScope | undefined,
): boolean {
  const rSize = scopeSize(recordScope);
  if (rSize === 0) return true; // enterprise record matches any query

  const qSize = scopeSize(queryScope);
  if (rSize > qSize) return false; // record is more specific than query — cannot be subset

  // Every key=value in the record scope must appear in the query scope
  if (!recordScope || !queryScope) return false;
  for (const [k, v] of Object.entries(recordScope)) {
    if (v === undefined || v === null) continue;
    if ((queryScope as Record<string, string | undefined>)[k] !== v)
      return false;
  }
  return true;
}

/**
 * Resolve records to a flat map of key → value.
 *
 * Algorithm per key:
 * 1. Exact scope match → use it
 * 2. Superset match (record scope is subset of query) → prefer most specific
 * 3. Enterprise fallback (no scope on record)
 * 4. Absent → omit from output
 * Tie-break at same specificity: higher trust wins
 * Null values excluded from output
 */
export function getBaselineValues(
  records: BaselineRecord[],
  scope?: BaselineScope,
): Record<string, number> {
  // Group records by key
  const byKey = new Map<string, BaselineRecord[]>();
  for (const r of records) {
    const existing = byKey.get(r.key);
    if (existing) {
      existing.push(r);
    } else {
      byKey.set(r.key, [r]);
    }
  }

  const result: Record<string, number> = {};

  for (const [key, candidates] of byKey) {
    // Filter to matching candidates (record scope is subset of query scope)
    // Exclude null-valued records so they don't outrank non-null estimated values
    const matching = candidates.filter(
      (r) => r.value != null && recordScopeIsSubsetOfQuery(r.scope, scope),
    );

    if (matching.length === 0) continue;

    // Pick the best candidate:
    // - prefer higher specificity (more dims in record scope)
    // - tie-break by trust (higher wins)
    let best: BaselineRecord | null = null;
    let bestSpecificity = -1;
    let bestTrust = -1;

    for (const r of matching) {
      const specificity = scopeSize(r.scope);
      const trust = SOURCE_TRUST[r.source] ?? 0;

      if (
        specificity > bestSpecificity ||
        (specificity === bestSpecificity && trust > bestTrust)
      ) {
        best = r;
        bestSpecificity = specificity;
        bestTrust = trust;
      }
    }

    if (best !== null && best.value !== null) {
      result[key] = best.value;
    }
  }

  return result;
}

/**
 * Single-key convenience wrapper around getBaselineValues.
 */
export function getBaselineValue(
  records: BaselineRecord[],
  key: string,
  scope?: BaselineScope,
): number | undefined {
  return getBaselineValues(records, scope)[key];
}

/**
 * Find by key + serialized scope. If found, replace. If not, append.
 * Returns a new array.
 */
export function upsertBaselineRecord(
  records: BaselineRecord[],
  next: BaselineRecord,
): BaselineRecord[] {
  const nextScopeKey = serializeBaselineScope(next.scope);
  const idx = records.findIndex(
    (r) =>
      r.key === next.key && serializeBaselineScope(r.scope) === nextScopeKey,
  );
  if (idx === -1) {
    return [...records, next];
  }
  const updated = [...records];
  updated[idx] = next;
  return updated;
}

/**
 * For each estimated record:
 * - If existing has a HIGHER trust record for same key+scope → keep existing.
 * - If same trust: update unless incoming value is null and existing is non-null.
 * - If lower trust or absent → use incoming.
 */
export function mergeEstimatedBaselineRecords(
  existing: BaselineRecord[],
  estimated: BaselineRecord[],
): BaselineRecord[] {
  let result = [...existing];

  for (const incoming of estimated) {
    const incomingScopeKey = serializeBaselineScope(incoming.scope);
    const existingIdx = result.findIndex(
      (r) =>
        r.key === incoming.key &&
        serializeBaselineScope(r.scope) === incomingScopeKey,
    );

    if (existingIdx === -1) {
      // No existing record — append incoming
      result = [...result, incoming];
    } else {
      const ex = result[existingIdx];
      if (!ex) continue;
      const existingTrust = SOURCE_TRUST[ex.source] ?? 0;
      const incomingTrust = SOURCE_TRUST[incoming.source] ?? 0;

      if (existingTrust > incomingTrust) {
        // Keep existing — higher trust
        continue;
      }

      if (existingTrust === incomingTrust) {
        // Same trust: do not overwrite non-null with null
        if (incoming.value === null && ex.value !== null) {
          continue;
        }
      }

      // Lower trust or same trust with valid incoming → replace
      const updated = [...result];
      updated[existingIdx] = incoming;
      result = updated;
    }
  }

  return result;
}

/**
 * Fraction of relevantKeys that have a non-null value with source
 * customer_reported or confirmed. A key is "complete" if ANY record
 * for that key (enterprise or scoped) is customer-provided with non-null value.
 */
export function computeBaselineCompleteness(
  records: BaselineRecord[],
  relevantKeys: string[],
): number {
  if (relevantKeys.length === 0) return 0;

  const highTrustSources = new Set<string>(["customer_reported", "confirmed"]);

  let count = 0;
  for (const key of relevantKeys) {
    // Count as complete if ANY record (enterprise or scoped) has high-trust non-null value
    const match = records.find(
      (r) =>
        r.key === key && r.value !== null && highTrustSources.has(r.source),
    );
    if (match) count++;
  }

  return count / relevantKeys.length;
}

/**
 * Multiply each encounter volume by applicable mix percentages.
 * If scope.specialty is set and specialty_mix has it, multiply by that %.
 * If scope.payer is set and payer_mix has it, multiply by that %.
 * If both, multiply by both (independent dimensions).
 * If neither applies, return original volumes unchanged.
 */
export function getScaledVolumes(
  encounterVolumes: Record<string, number>,
  scope: BaselineScope,
  mixes: {
    specialty_mix?: Record<string, number>;
    payer_mix?: Record<string, number>;
  },
): Record<string, number> {
  let specialtyFactor = 1;
  let payerFactor = 1;

  if (scope?.specialty && mixes.specialty_mix) {
    const mix = mixes.specialty_mix[scope.specialty];
    if (mix !== undefined) specialtyFactor = mix;
  }

  if (scope?.payer && mixes.payer_mix) {
    const mix = mixes.payer_mix[scope.payer];
    if (mix !== undefined) payerFactor = mix;
  }

  const factor = specialtyFactor * payerFactor;

  if (factor === 1) return encounterVolumes;

  return Object.fromEntries(
    Object.entries(encounterVolumes).map(([k, v]) => [
      k,
      Math.round(v * factor),
    ]),
  );
}

// ── Dimension ID → BaselineScope field mapping ─────────────
const DIMENSION_SCOPE_MAP: Record<string, keyof NonNullable<BaselineScope>> = {
  payer_type: "payer",
  care_setting: "setting",
  care_setting_ip_op: "setting",
  specialty: "specialty",
  specialty_excl_ed: "specialty",
  facility: "site",
  service_line: "specialty",
  site: "site",
};

/**
 * Resolve baseline records for a deployment scope.
 *
 * For each dimension in the scope where not all options are selected:
 * - If exactly one option selected: resolve baselines with that single scope filter
 * - If multiple options selected: resolve per-option, weighted-average by volume_weight
 *
 * Returns a new BaselineRecord[] with enterprise records replaced by scope-resolved values
 * where scoped data exists. Records without scoped values fall back to enterprise.
 *
 * Dimension ID to BaselineScope field mapping:
 * - "payer_type" -> scope.payer
 * - "care_setting" -> scope.setting
 * - "specialty" -> scope.specialty
 * - "site" -> scope.site
 */
export function resolveBaselineRecordsForScope(
  records: BaselineRecord[],
  deploymentScope: DeploymentScope,
): BaselineRecord[] {
  // 1. Figure out which dimensions are actually scoped (not all options selected)
  const scopedDimensions = deploymentScope.dimensions.filter((dim) => {
    const scopeField = DIMENSION_SCOPE_MAP[dim.dimension_id];
    if (!scopeField) return false; // unknown dimension, skip
    const allSelected = dim.options.every((o) => o.selected);
    if (allSelected) return false; // all selected = no scoping
    const anySelected = dim.options.some((o) => o.selected);
    return anySelected; // at least one selected
  });

  if (scopedDimensions.length === 0) return records;

  // Separate enterprise records (no scope) from already-scoped records
  const enterpriseRecords: BaselineRecord[] = [];
  const nonEnterpriseRecords: BaselineRecord[] = [];
  for (const r of records) {
    if (scopeSize(r.scope) === 0) {
      enterpriseRecords.push(r);
    } else {
      nonEnterpriseRecords.push(r);
    }
  }

  // Group enterprise records by key
  const enterpriseByKey = new Map<string, BaselineRecord>();
  for (const r of enterpriseRecords) {
    // Keep last per key (mirrors how getBaselineValues picks from candidates)
    enterpriseByKey.set(r.key, r);
  }

  // For each enterprise record key, try to resolve via scoped dimensions
  const resolvedEnterprise: BaselineRecord[] = [];

  for (const [key, enterpriseRecord] of enterpriseByKey) {
    let resolved = false;

    // Try each scoped dimension (process first matching dimension)
    for (const dim of scopedDimensions) {
      const scopeField = DIMENSION_SCOPE_MAP[dim.dimension_id];
      if (!scopeField) continue;
      const selectedOptions = dim.options.filter((o) => o.selected);

      // Collect scoped values + weights for selected options that have data
      const scopedEntries: Array<{ value: number; weight: number }> = [];
      let totalWeight = 0;

      for (const opt of selectedOptions) {
        // Find ALL matching scoped records for this option, then pick the most specific.
        // A composite record {payer: "commercial", specialty: "cardiology"} is more specific
        // than {payer: "commercial"} alone — prefer it when both dimensions are active.
        const candidates = records.filter(
          (r) =>
            r.key === key &&
            r.scope != null &&
            (r.scope as Record<string, string | undefined>)[scopeField] ===
              opt.option_id &&
            r.value !== null &&
            Object.entries(r.scope as Record<string, string | undefined>).every(
              ([dimField, val]) => {
                if (dimField === scopeField || val == null || val === "")
                  return true;
                const otherDim = scopedDimensions.find(
                  (d) => DIMENSION_SCOPE_MAP[d.dimension_id] === dimField,
                );
                return otherDim
                  ? otherDim.options.some(
                      (o) => o.selected && o.option_id === val,
                    )
                  : true;
              },
            ),
        );

        if (candidates.length > 0) {
          // Pick the most specific (most scope dimensions), then highest trust
          candidates.sort((a, b) => {
            const specDiff = scopeSize(b.scope) - scopeSize(a.scope);
            if (specDiff !== 0) return specDiff;
            return (
              (SOURCE_TRUST[b.source] ?? 0) - (SOURCE_TRUST[a.source] ?? 0)
            );
          });
          scopedEntries.push({
            value: candidates[0]?.value ?? 0,
            weight: opt.volume_weight,
          });
          totalWeight += opt.volume_weight;
        }
      }

      if (scopedEntries.length === 0) continue; // no scoped data for this dimension

      // Check if this baseline is a count (should be summed) or a rate (should be averaged)
      const def = getBaselineDefinition(key);
      const isCount = def != null && def.unit === "count";

      let resolvedValue: number;
      if (isCount) {
        // Sum all scoped count values
        resolvedValue = Math.round(
          scopedEntries.reduce((sum, e) => sum + e.value, 0),
        );
      } else {
        // Weighted average for rates/percentages/indices
        resolvedValue = scopedEntries.reduce(
          (sum, e) => sum + e.value * (e.weight / totalWeight),
          0,
        );
      }

      resolvedEnterprise.push({
        ...enterpriseRecord,
        value: resolvedValue,
      });
      resolved = true;
      break; // stop at first dimension that provides scoped data
    }

    if (!resolved) {
      // Clone to avoid mutating shared references (Bug 5)
      resolvedEnterprise.push({ ...enterpriseRecord });
    }
  }

  // Volume scaling is NOT done here — it's a compute concern.
  // The YAML engine handles volume scaling via scope_fractions.
  // The custom model path handles it via getScaledVolumes() if needed.

  return [...resolvedEnterprise, ...nonEnterpriseRecords];
}

/**
 * Build a flat formula context map from an org profile + baseline records.
 * Combines baseline values (resolved via getBaselineValues) with org-level context variables.
 */
export function buildFormulaContext(
  profile: OrganizationProfileData,
  scope?: BaselineScope,
  extraContext?: Record<string, number>,
): Record<string, number> {
  const ctx: Record<string, number> = {};

  // Baseline values
  const baselineVals = getBaselineValues(profile.baselineRecords, scope);
  Object.assign(ctx, baselineVals);

  // ── Org-level context using registry-standard snake_case keys ──
  // The LLM prompt tells the model to use keys from BASELINE_REGISTRY,
  // so we emit all org data under those exact keys.

  // Encounter volumes → registry keys (fallback only — don't overwrite
  // baseline-derived values).
  //
  // Zero is a real volume: an inpatient-only facility legitimately reports 0
  // ambulatory visits. The sub-volume keys below have no `BASELINE_REGISTRY`
  // entry and no estimator-produced baseline record, so this block is their
  // only producer — gating emission on truthiness would drop the key outright,
  // and `missingContextKeys` checks presence alone, so editing a volume to 0
  // would un-resolve every saved model referencing it (same contract as the
  // workforce keys below). Only values that could never be a volume —
  // non-finite or negative — are dropped.
  if (profile.encounter_volumes) {
    const ev = profile.encounter_volumes;
    const total = Object.values(ev).reduce((a, b) => a + (b ?? 0), 0);
    if (!("total_encounters" in ctx)) ctx.total_encounters = total;
    const emitVolume = (key: string, value: number | undefined) => {
      if (
        typeof value === "number" &&
        Number.isFinite(value) &&
        value >= 0 &&
        !(key in ctx)
      ) {
        ctx[key] = value;
      }
    };
    emitVolume("inpatient_discharges", ev.inpatient);
    emitVolume("outpatient_visits", ev.outpatient);
    emitVolume("ed_visits", ev.emergency);
    emitVolume("ambulatory_visits", ev.ambulatory);
    emitVolume("radiology_encounters", ev.radiology);
    emitVolume("surgical_cases", ev.surgical);
  }

  // Workforce FTEs + average fully-loaded cost. The cost key is what lets a
  // generated formula price staff time against the organization's own numbers
  // instead of an invented `a.<id>` hourly-rate assumption (ELI-1068).
  //
  // This context is the RESOLVABILITY surface: a saved model lists the keys it
  // references in `definition.contextKeys`, and `missingContextKeys` checks
  // presence alone. A key that disappears downgrades the model to
  // definition-only and clears its computed metrics, so a legitimate zero must
  // still emit its key. Deciding which keys are worth SHOWING the generator is
  // a separate concern, owned by `buildAvailableBaselineDescriptors`. Only
  // values that could never be correct — non-finite, or negative, which
  // `validateWorkforce` already rejects on write — are dropped here.
  if (profile.workforce) {
    const workforceEntries = Object.entries(profile.workforce);
    const literalFteKeys = new Set(
      workforceEntries.map(([role]) => workforceFteContextKey(role)),
    );

    // Emit literal role FTEs first. Existing profiles may contain a role named
    // `<x>_cost`, whose FTE key collides with role `<x>`'s cost key. Literal
    // membership preserves the historical pre-cost-key meaning and must win.
    for (const [role, entry] of workforceEntries) {
      if (entry?.ftes != null) {
        ctx[workforceFteContextKey(role)] = entry.ftes;
      }
    }

    // Emit costs only when they do not occupy another literal role's FTE key.
    // This keeps reads non-fatal for pre-existing colliding profiles and makes
    // the result independent of object insertion order.
    for (const [role, entry] of Object.entries(profile.workforce)) {
      const cost = entry?.avg_fully_loaded_cost;
      const costKey = workforceCostContextKey(role);
      if (
        !literalFteKeys.has(costKey) &&
        typeof cost === "number" &&
        Number.isFinite(cost) &&
        cost >= 0
      ) {
        ctx[costKey] = cost;
      }
    }
  }

  // DB-level org fields → registry keys
  // Org-context values (bedCount, annualRevenue, etc.) are authoritative and
  // must override any stale legacy baseline record that happens to share the
  // same key.  Without the override, a legacy "bed_count" baseline record
  // written before the field was reclassified as org_context would shadow the
  // current org-profile value, causing formulas to compute against stale data
  // after a customer edits licensed beds in Identity & Settings.
  if (extraContext) {
    for (const [key, value] of Object.entries(extraContext)) {
      // Emit under the registry snake_case key
      const snake = key.replace(/[A-Z]/g, (ch) => `_${ch.toLowerCase()}`);
      // Org-context keys always override — they are the source of truth
      const def = getBaselineDefinition(key) ?? getBaselineDefinition(snake);
      const isOrgContext = def?.class === "org_context";
      if (isOrgContext || !(snake in ctx)) ctx[snake] = value;
      if (isOrgContext || !(key in ctx)) ctx[key] = value;
    }
  }

  return ctx;
}

/** Org-profile fields needed to rebuild a model's formula context. */
export interface BuildContextProfileInput {
  profileData?: unknown;
  baselineRecords?: unknown;
  bedCount?: number | null;
  annualRevenue?: number | bigint | null;
  physicianCount?: number | null;
}

/**
 * Build the full org-derived formula context from a stored org-profile row
 * (the DB-row shape, where `profileData` is an untyped jsonb blob). Adapts the
 * row into the `OrganizationProfileData` shape `buildFormulaContext` expects and
 * injects the DB-column fields (bedCount/annualRevenue/physicianCount) as extra
 * context. This is the single source of truth for "org profile → context",
 * shared by model generation, server recompute, and the frontend live ROI.
 */
export function buildContextFromProfile(
  profile: BuildContextProfileInput,
): Record<string, number> {
  const pd =
    profile.profileData &&
    typeof profile.profileData === "object" &&
    !Array.isArray(profile.profileData)
      ? (profile.profileData as Partial<ProfileData>)
      : {};
  const records = (profile.baselineRecords ?? []) as BaselineRecord[];
  const orgProfile = {
    ...pd,
    baselineRecords: records,
  } as OrganizationProfileData;

  const extra: Record<string, number> = {};
  if (profile.bedCount != null) extra.bedCount = Number(profile.bedCount);
  if (profile.annualRevenue != null)
    extra.annualRevenue = Number(profile.annualRevenue);
  if (profile.physicianCount != null) {
    extra.physician_count = Number(profile.physicianCount);
  }

  return buildFormulaContext(orgProfile, undefined, extra);
}

// `resolveModelContext` lives in `../profile/model-context` — it must build the
// org context from the EFFECTIVE (facts-applied) profile, which depends on this
// module's `buildContextFromProfile`. Keeping it there avoids a baselines ⇄
// profile import cycle. Re-exported from the package barrel so importers are
// unaffected.
